import {
  Button,
  Dialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {LeasingRateMatrixCell} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {leasingCellFormSchema, LeasingCellFormValues} from './schemas/leasingCellFormSchema';

interface LeasingCellDialogProps {
  isOpen: boolean;
  term: number;
  annualMileage: number;
  cell: LeasingRateMatrixCell | null;
  currency: string;
  isActive: boolean;
  isReadOnly: boolean;
  onClose: () => void;
  onSave: (cell: LeasingRateMatrixCell, setActive: boolean) => void;
  onDelete: (cell: LeasingRateMatrixCell) => void;
}

export function LeasingCellDialog(props: LeasingCellDialogProps) {
  const formatCurrency = useFormatCurrency();
  const monthlyRateCurrency = props.cell?.monthlyRate.currency ?? props.currency;

  const handleSubmit: FormSubmitHandler<LeasingCellFormValues> = (values) => {
    props.onSave(
      {
        term: props.term,
        annualMileage: props.annualMileage,
        monthlyRate: {amount: String(values.monthlyRateAmount), currency: monthlyRateCurrency},
        nominalRate: isNil(values.nominalRate) ? null : String(values.nominalRate),
        apr: isNil(values.apr) ? null : String(values.apr),
      },
      values.setActive
    );

    return Promise.resolve();
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t(
        props.isReadOnly
          ? 'page.vehicle.financingAndLeasing.configured'
          : 'page.vehicle.financingAndLeasing.editLeasingRate'
      )}
      size="small"
    >
      <Form<LeasingCellFormValues>
        experimentalZodSchema={leasingCellFormSchema}
        mode="onChange"
        defaultValues={{
          monthlyRateAmount: isNil(props.cell) ? undefined : Number(props.cell.monthlyRate.amount),
          nominalRate: isNotNil(props.cell?.nominalRate) ? Number(props.cell?.nominalRate) : null,
          apr: isNotNil(props.cell?.apr) ? Number(props.cell?.apr) : null,
          setActive: props.isActive,
        }}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const monthlyRateAmount = formApi.watch('monthlyRateAmount');
          const interestRatesAreRequired =
            isNotNil(formApi.watch('nominalRate')) || isNotNil(formApi.watch('apr'));

          // The paired required rule lives on the sibling field, so editing one interest rate has
          // to revalidate both of them for the error to appear and disappear as the user types.
          const revalidateInterestRates = () => {
            void formApi.trigger(['nominalRate', 'apr']);
          };

          return (
            <VStack spacing={3}>
              <HStack spacing={2} wrap>
                <Text>
                  <strong>
                    {i18n.t('page.vehicle.financingAndLeasing.monthsValue', {term: props.term})}
                  </strong>
                </Text>
                <Text color="secondary">·</Text>
                <Text>
                  <strong>
                    {i18n.t('page.vehicle.financingAndLeasing.mileagePerYear', {
                      mileage: props.annualMileage,
                    })}
                  </strong>
                </Text>
              </HStack>

              <FormField
                control={control}
                name="monthlyRateAmount"
                type="currency"
                label={i18n.t('page.vehicle.financingAndLeasing.monthlyRateGross')}
                currency={isCurrency(monthlyRateCurrency) ? monthlyRateCurrency : undefined}
                isDisabled={props.isReadOnly}
                isRequired
              />

              <Grid columns={[1, 2]}>
                <FormField
                  control={control}
                  name="nominalRate"
                  type="number"
                  label={i18n.t('page.vehicle.financingAndLeasing.nominalRate')}
                  suffix="%"
                  decimalPlaces={2}
                  minStepperValue={0}
                  isDisabled={props.isReadOnly}
                  isRequired={interestRatesAreRequired}
                  onChange={revalidateInterestRates}
                />
                <FormField
                  control={control}
                  name="apr"
                  type="number"
                  label={i18n.t('page.vehicle.financingAndLeasing.apr')}
                  suffix="%"
                  decimalPlaces={2}
                  minStepperValue={0}
                  isDisabled={props.isReadOnly}
                  isRequired={interestRatesAreRequired}
                  onChange={revalidateInterestRates}
                />
              </Grid>

              <Box
                backgroundColor="palettes.green.10.100"
                border="1px solid"
                borderColor="palettes.green.60.100"
                borderRadius="medium"
                padding={3}
                data-testid="leasing-cell-result"
              >
                <VStack spacing={1}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('page.vehicle.financingAndLeasing.totalOverTerm')}
                  </Text>
                  <Heading size={2} color="success">
                    {formatCurrency((monthlyRateAmount ?? 0) * props.term, monthlyRateCurrency)}
                  </Heading>
                </VStack>
              </Box>

              <Text size="xSmall" color="secondary">
                {i18n.t('page.vehicle.financingAndLeasing.grossRateHint')}
              </Text>
              <Show when={!props.isReadOnly}>
                <FormField
                  control={control}
                  name="setActive"
                  type="checkbox"
                  label={i18n.t('page.vehicle.financingAndLeasing.setAsActiveVariant')}
                  isDisabled={props.isActive}
                />
              </Show>
              <Show when={!props.isReadOnly}>
                <HStack justify="space-between" spacing={3}>
                  <Box>
                    <Show when={props.cell !== null}>
                      <Button
                        title={i18n.t('general.actions.delete')}
                        variant="dangerOutlined"
                        leftIcon="action/delete"
                        aria-label={i18n.t('general.actions.delete')}
                        onClick={() => {
                          if (isNotNil(props.cell)) {
                            props.onDelete(props.cell);
                          }
                        }}
                      />
                    </Show>
                  </Box>
                  <HStack spacing={2}>
                    <Button
                      title={i18n.t('general.actions.cancel')}
                      variant="secondary"
                      onClick={props.onClose}
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.save')}
                      isDisabled={!formApi.formState.isValid}
                    />
                  </HStack>
                </HStack>
              </Show>
            </VStack>
          );
        }}
      </Form>
    </Dialog>
  );
}
